/**
 * ISO 3166-1 alpha-2 country options for billing / postal address fields.
 *
 * The backend stores and validates a two-letter code, so the UI must never let a free-text country
 * through: "Osterreich", "AUT" and "Austria" all fail validation the same silent way, and the
 * address then saves without a country at all.
 *
 * Ordered Austria-first, then the neighbours we actually bill in, then the rest of the EU/EEA and
 * the two non-European codes already in use. Add codes as customers appear rather than shipping the
 * full 249-entry ISO table nobody scrolls through.
 *
 * Lives in shared-lib because the same address is captured in more than one app — operator company
 * management, the hotline registration flow, the organization portal — and a per-app copy is how the
 * lists drift until the same customer can be created under two different country codes.
 *
 * Distinct from PLATE_COUNTRIES, which is about *rendering* a license plate (EU band, district
 * split) and only covers the countries whose plate layout is implemented. Do not merge them: a
 * customer can be billed in a country whose plates this platform never draws.
 */
export interface CountryOption {
    /** ISO 3166-1 alpha-2, uppercase — exactly what is persisted. */
    code: string;
    /** English display name. */
    name: string;
}

export const COUNTRIES: readonly CountryOption[] = [
    { code: 'AT', name: 'Austria' },
    { code: 'DE', name: 'Germany' },
    { code: 'CH', name: 'Switzerland' },
    { code: 'LI', name: 'Liechtenstein' },
    { code: 'IT', name: 'Italy' },
    { code: 'FR', name: 'France' },
    { code: 'BE', name: 'Belgium' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'LU', name: 'Luxembourg' },
    { code: 'CZ', name: 'Czechia' },
    { code: 'SK', name: 'Slovakia' },
    { code: 'HU', name: 'Hungary' },
    { code: 'SI', name: 'Slovenia' },
    { code: 'HR', name: 'Croatia' },
    { code: 'PL', name: 'Poland' },
    { code: 'DK', name: 'Denmark' },
    { code: 'SE', name: 'Sweden' },
    { code: 'FI', name: 'Finland' },
    { code: 'NO', name: 'Norway' },
    { code: 'IE', name: 'Ireland' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'ES', name: 'Spain' },
    { code: 'PT', name: 'Portugal' },
    { code: 'GR', name: 'Greece' },
    { code: 'RO', name: 'Romania' },
    { code: 'BG', name: 'Bulgaria' },
    { code: 'EE', name: 'Estonia' },
    { code: 'LV', name: 'Latvia' },
    { code: 'LT', name: 'Lithuania' },
    { code: 'US', name: 'United States' },
    { code: 'CA', name: 'Canada' },
];

/** The code a new address starts on. */
export const DEFAULT_COUNTRY_CODE = 'AT';

/**
 * Display name for a stored code, falling back to the code itself.
 *
 * Organizations imported from the old platform can carry a code this list does not cover yet;
 * showing "XK" is honest, while showing an empty cell would read as "no country on file".
 */
export function countryName(code: string | null | undefined): string {
    const normalized = code?.trim().toUpperCase();
    if (!normalized) return '';
    return COUNTRIES.find(c => c.code === normalized)?.name ?? normalized;
}
